import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uuid from 'uuid';
import { compose } from 'recompose';
import { Loader, Card, Divider } from 'semantic-ui-react';
import Pagination from 'dyzio-shared-components/lib/components/pagination';

import createNewCampaign from '../../utils/create-new-campaign';

import CampaignsTable from '../../components/campaigns-table';
import TabActionBar, { ViewModes } from '../../components/tab-action-bar';
import CampaignCard from '../../components/campaign-card';
import DyzioEmptyState from '../../components/dyzio-empty-state';
import AddCampaignModal from '../../components/new-add-campaign-modal';
import { DyzioWarningMessage } from '../../components/dyzio-messages';

import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';
import hasViewModeManagement from '../../hocs/has-viewmode-management';

const CAMPAIGN_VERSION_TWO = 2;
const DEFAULT_PER_PAGE = 8;
const HELP_PAGE_URL = 'https://help.dyzio.co/campaigns/campaign-list';

const perPageOptions = [
  { key: '10', text: '10', value: 10 },
  { key: '20', text: '20', value: 20 },
  { key: '50', text: '50', value: 50 },
];

const campaignTypesOptions = [
  {
    value: 'AUTOMATED',
    text: 'Automated Campaign',
    key: 'AUTOMATED',
  },
  {
    value: 'ACR',
    text: 'Automated Channel Report',
    key: 'ACR',
  },
  {
    value: 'WORKFLOW',
    text: 'Manual',
    key: 'WORKFLOW',
  },
  {
    value: 'all',
    text: 'All',
    key: 'ALL',
  },
];

const campaignStates = [
  { key: 'ACTIVE', text: 'Active', value: 'ACTIVE' },
  { key: 'ARCHIVED', text: 'Archived', value: 'ARCHIVED' },
];

const sortByOptions = [
  { key: 'assetsCountAsc', text: 'Assets count (ASC)', value: { field: 'totalAssetsCount', order: 'ASC' } },
  { key: 'assetsCountDesc', text: 'Assets count (DESC)', value: { field: 'totalAssetsCount', order: 'DESC' } },
  {
    key: 'createdDateAsc',
    text: 'Created date (ASC)',
    value: {
      field: 'campaignCreatedDate',
      order: 'ASC',
    },
  },
  {
    key: 'createdDateDesc',
    text: 'Created date (DESC)',
    value: {
      field: 'campaignCreatedDate',
      order: 'DESC',
    },
  },
  { key: 'nameAsc', text: 'Name (ASC)', value: { field: 'campaignName', order: 'ASC' } },
  { key: 'nameDesc', text: 'Name (DESC)', value: { field: 'campaignName', order: 'DESC' } },
];

const INITIAL_FORM_STATE_FIELDS = {
  name: null,
  budget: null,
  liveDate: null,
  hashtags: null,
  endDate: null,
  version: 2,
  hasChanged: false,
  savedOk: false,
  isValidBudget: true,
};

class CampaignsList extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props) {
    super(props);

    console.log('CampaignsList constructor');
    const savedViewMode = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAGINS_LIST_VIEW_MODE);
    const perPage = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAIGNS_LIST_PER_PAGE);
    const sortBy = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAIGNS_LIST_SORT_BY);
    const campaignFilter = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAIGNS_LIST_FILTER);
    this.state = {
      isLoadingCampaigns: true,
      isLoading: true,
      isFailed: false,
      isLoaded: false,
      isTimeoutError: false,
      records: [],
      totalRecords: 0,
      campaignFilter: campaignFilter.value || 'ACTIVE',
      viewMode: props.viewModeManager.getViewMode(savedViewMode),
      currentPage: 1,
      search: {

      },
      perPage: perPage.value || DEFAULT_PER_PAGE,
      addCampaignModalVisible: false,
      sort: sortBy.value || {
        field: 'campaignCreatedDate',
        order: 'DESC',
      },
      filter: {
        version: CAMPAIGN_VERSION_TWO,
        campaignType: 'all',
      },
      // new campaign fields
      ...INITIAL_FORM_STATE_FIELDS,
    };
  }

  componentDidMount = () => {
    console.log('CampaignsList componentDidMount');
    this.requestData();
  }

  onSortByChanged = (event, data) => {
    const { value } = data;
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      sort: value,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAIGNS_LIST_SORT_BY, value);
      this.requestData();
    });
  }

  onViewModeChanged = (event, data) => {
    const { name } = data;
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      viewMode: name,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAGINS_LIST_VIEW_MODE, name);
    });
  }

  onInputChange = (event, { name, value }) => {
    this.setState({
      [name]: value,
      hasChanged: true,
      savedOk: false,
    });
  }

  onLiveDateChange = date => {
    this.setState({
      liveDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onEndDateChange = date => {
    this.setState({
      endDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onDeleteCampaign = id => {
    if (id) {
      this.updateCampaign(id, {
        attributes: {
          status: 'DELETED',
        },
      });
    }
  }

  onArchiveCampaign = async id => {
    if (id) {
      const { restClient, userInfo: { organisationId } } = this.props;
      await restClient({
        method: 'DELETE',
        url: `/organisations/${organisationId}/cache`,
        baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
      });
      this.updateCampaign(id, {
        attributes: {
          status: 'ARCHIVED',
        },
      });
    }
  }

  onUnarchiveCampaign = id => {
    if (id) {
      this.updateCampaign(id, {
        attributes: {
          status: 'ACTIVE',
        },
      });
    }
  }

  onCurrencyChange = (event, data) => {
    const { value } = data;
    this.setState({
      currency: value,
    });
  }

  onPageIncrement = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage + 1,
    }), this.requestData);
  }

  onPageDecrement = () => {
    this.setState(prevState => ({
      currentPage: prevState.currentPage - 1,
    }), this.requestData);
  }

  onPerPageValueChanged = (event, { value }) => {
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      perPage: value,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAIGNS_LIST_PER_PAGE, value);
      this.requestData();
    });
  }

  onSearchInputSubmit = value => {
    this.setState({
      search: {
        searchTerm: value,
      },
    }, this.requestData);
  }

  onSearchCleared = () => {
    this.setState({
      search: {},
    }, this.requestData);
  }

  toggleAddNewCampaignModalState = () => {
    const { addCampaignModalVisible } = this.state;
    const updatedState = {
      addCampaignModalVisible: !addCampaignModalVisible,
    };
    if (addCampaignModalVisible) {
      _.assign(updatedState, INITIAL_FORM_STATE_FIELDS);
    }
    this.setState(updatedState);
  }

  requestData = () => {
    const { restClient, userInfo } = this.props;
    const { userId, organisationId, isUserSuperAdmin } = userInfo;
    const { sort, currentPage, perPage, search, campaignFilter, filter } = this.state;

    const campaignType = _.get(filter, 'campaignType', null);

    let options = {
      perPage,
      currentPage,
      search,
      sort,
      filter: {
        campaignPermissionStatus: campaignFilter,
        campaignType: campaignType && campaignType !== 'all' ? campaignType : undefined,
      },
    };

    // if there is a search then force the results page back to the start
    // should use setState here or disable the pagination controls on a search
    if (search.searchTerm) options.currentPage = 1;

    let campaignQueryUrl;

    if (isUserSuperAdmin) {
      campaignQueryUrl = `/organisations/${organisationId}/campaigns?q=`;
    } else {
      options = _.assignIn(options, { userId });
      campaignQueryUrl = '/campaigns/query?q=';
    }

    const queryParams = encodeURIComponent(JSON.stringify(options));
    this.setState({
      isLoaded: false,
      isFailed: false,
      isLoading: true,
    }, () => {
      const restRequest = restClient({
        method: 'get',
        url: `${campaignQueryUrl}${queryParams}`,
        baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
      }).then(response => {
        const { data: { data: { data, results } } } = response;
        return this.setState({
          records: data,
          isLoadingCampaigns: false,
          totalRecords: results,
        });
      });

      const orgPromise = restClient.get(`organisations/${organisationId}`)
        .then(organisationResource => {
          const {
            data: {
              data: { attributes },
            },
          } = organisationResource;
          const { defaultCurrency } = attributes;
          return this.setState({ organisation: attributes, defaultCurrency, currency: defaultCurrency });
        });

      Promise.all([restRequest, orgPromise])
        .then(() => this.setState({
          isLoaded: true,
          isFailed: false,
          isLoading: false,
        }))
        .catch(error => {
          this.setState({
            isLoadingCampaigns: false,
            isLoaded: true,
            isTimeoutError: error && _.includes(error.toString(), 'timeout'),
            isFailed: true,
            isLoading: false,
          });
        });
    });
  }

  onCampaignFilterChanged = (event, { value }) => {
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      campaignFilter: value,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAIGNS_LIST_FILTER, value);
      this.requestData();
    });
  }

  updateCampaign = (id, data) => {
    const { restClient } = this.props;
    restClient.patch(`/campaignPermission/${id}`, {
      data,
    }).then(() => {
      this.requestData();
    });
  }

  onAddCampaignSubmit = values => {
    const {
      organisation,
    } = this.state;

    const {
      jsonAPIClient,
      userInfo,
    } = this.props;

    return createNewCampaign({
      ...values,
      jsonAPIClient,
      userInfo,
      organisation,
    });
  };

  onCamapignTypeFilterChanged = (event, { value }) => {
    const { filter } = this.state;
    const newFilter = { ...filter, campaignType: value };
    this.setState({
      filter: newFilter,
    }, this.requestData);
  }

  getCampaignTypeFilterMessage = () => {
    const { filter } = this.state;
    if (filter.campaignType && filter.campaignType !== 'all') {
      const option = _.find(campaignTypesOptions, ['value', filter.campaignType]);
      return _.get(option, 'text', '');
    }
    return '';
  }


  render() {
    const actionButtonProps = {
      onClick: this.toggleAddNewCampaignModalState,
      content: 'Add a campaign',
    };

    const { userInfo: { email, name: userName, userId, isUserSuperAdmin } } = this.props;

    const messageProps = {
      email, userName, userId,
    };

    const {
      isLoading,
      isLoaded,
      isFailed,
      records,
      viewMode,
      addCampaignModalVisible,
      currentPage,
      perPage,
      totalRecords,
      sort,
      campaignFilter,
      isLoadingCampaigns,
      search: { searchTerm },
      isTimeoutError,
    } = this.state;

    const totalPages = _.ceil(totalRecords / perPage);
    const isSearchContext = searchTerm && searchTerm.length > 0;

    const isLargeCardsViewMode = viewMode === ViewModes.cardsLarge;
    const isSmallCardsViewMode = viewMode === ViewModes.cardsSmall;
    const isTableView = viewMode === ViewModes.table;
    const isCardsViewMode = isLargeCardsViewMode || isSmallCardsViewMode;
    const noCampaignPermissions = _.isEmpty(records);
    const isLoadSuccess = isLoaded && !isFailed && !noCampaignPermissions;
    const isLoadEmptyData = isLoaded && !isFailed && noCampaignPermissions;

    return (
      <div>
        <TabActionBar
          hasSmallCardsView={false}
          hasSearchBox
          hasCampaignTypeFilter
          hasCampaignFilter
          hasActionButton
          hasPerPageSelector
          actionButtonProps={actionButtonProps}
          onViewModeChanged={this.onViewModeChanged}
          shouldDisableButtons={isLoading || noCampaignPermissions}
          isLargeCards={isLargeCardsViewMode}
          isSmallCards={isSmallCardsViewMode}
          isTableView={isTableView}
          sortByOptions={sortByOptions}
          campaignTypesOptions={campaignTypesOptions}
          sort={sort}
          onCamapignTypeFilterChanged={this.onCamapignTypeFilterChanged}
          onSortByChanged={this.onSortByChanged}
          onPerPageValueChanged={this.onPerPageValueChanged}
          onSearchInputSubmit={this.onSearchInputSubmit}
          onSearchCleared={this.onSearchCleared}
          onCampaignFilterChanged={this.onCampaignFilterChanged}
          perPage={perPage}
          helpPageUrl={HELP_PAGE_URL}
          perPageOptions={perPageOptions}
          campaignFilterOptions={campaignStates}
          campaignFilter={campaignFilter}
        />
        { isLoading && <Loader active content="Loading campaigns..." /> }
        {
          isTimeoutError && <DyzioWarningMessage
            header="Sorry... Your request timed out!"
            content="Our tech team has been informed and will look into the issue for you."
            reportMessage="Loading campaigns timeout"
            reportMessageArgs={messageProps}
          />
        }
        {
          isLoadEmptyData &&
          <DyzioEmptyState
            isSearchContext={isSearchContext}
            buttonLabel="Add a campaign"
            helpUrl="https://help.dyzio.co/en/articles/5089448-how-to-create-a-campaign"
            context="CAMPAIGNS"
            onClick={this.toggleAddNewCampaignModalState}
            content={
              <div>
                <p>{ !isSearchContext ?
                  `You have no ${this.getCampaignTypeFilterMessage()} campaigns.`
                  : 'No search results!' }
                </p>
                {
                  !isSearchContext &&
                  <p>Use the button below to start your first {this.getCampaignTypeFilterMessage()} campaign.</p>
                }
              </div>
            }
          />
        }
        {
          isLoadSuccess && isCardsViewMode &&
          <Card.Group itemsPerRow={isLargeCardsViewMode ? 4 : 6}>
            { _.map(records, record => <CampaignCard
              listType={campaignFilter}
              key={uuid()}
              {...record}
              onDelete={this.onDeleteCampaign}
              onArchive={this.onArchiveCampaign}
              onUnarchive={this.onUnarchiveCampaign}
              isUserSuperAdmin={isUserSuperAdmin}
            />)}
          </Card.Group>
        }
        {
          isLoadSuccess && isTableView &&
            <CampaignsTable
              records={records}
              isLoadingCampaigns={isLoadingCampaigns}
              onDelete={this.onDeleteCampaign}
              onArchive={this.onArchiveCampaign}
              onUnarchive={this.onUnarchiveCampaign}
              listType={campaignFilter}
              isUserSuperAdmin={isUserSuperAdmin}
            />
        }
        {
          isLoadSuccess &&
          <div>
            <Divider />
            <Pagination
              onPageIncrement={this.onPageIncrement}
              onPageDecrement={this.onPageDecrement}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </div>
        }
        {
          addCampaignModalVisible &&
          <AddCampaignModal
            onClose={this.toggleAddNewCampaignModalState}
            onAddCampaignSubmit={this.onAddCampaignSubmit}
            allowCampaignTypeChange
          />
        }
      </div>
    );
  }
}

CampaignsList.propTypes = {
  dyzioStorage: PropTypes.object.isRequired,
  viewModeManager: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
  restClient: PropTypes.func.isRequired,
};


export default compose(
  hasRESTClient,
  hasJSONAPIClient,
  hasUserInfo,
  hasLocalStorageClient,
  hasViewModeManagement,
)(CampaignsList);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-list/index.js
 **/